import React, { useEffect, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";
import MiniSparklineSVG from "@/components/MiniSparklineSVG";

/**
 * Iter 80 — SymbolSentimentChip.
 * Iter 81 — enriched with 24h trend arrow.
 * Iter 82 — chip is now a Popover trigger that renders a 7-day
 *           bull%-bear% sparkline so users see the trajectory
 *           that led to the current tone.
 *
 * Props:
 *   symbol   : uppercase ticker string
 *   sentiment: latest snapshot from /market/sentiment/latest, or null
 */
export function SymbolSentimentChip({ symbol, sentiment }) {
  const [open, setOpen] = useState(false);
  const [historySeries, setHistorySeries] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  // Iter 85 — Tick Value Chip. Track which sparkline tick is focused
  // so keyboard users see the same info as hover users (chip
  // rendered inline next to the sparkline).
  // Iter 86 — Tick Chip Auto-Position. Also track the tick's x-pct
  // within the sparkline so the chip anchors under the focused
  // point (with edge clamping so it never overflows).
  const [focusedTick, setFocusedTick] = useState(null);
  const [focusedTickX, setFocusedTickX] = useState(null);

  useEffect(() => {
    if (!open || historySeries || loadingHistory) return;
    setLoadingHistory(true);
    api.get(`/market/sentiment/history/${symbol}?days=7`)
      .then((r) => setHistorySeries(r.data?.series || []))
      .catch(() => setHistorySeries([]))
      .finally(() => setLoadingHistory(false));
  }, [open, symbol, historySeries, loadingHistory]);

  if (!sentiment) {
    return (
      <span
        data-testid={`sentiment-chip-${symbol}`}
        data-tone="none"
        className="mono text-[9px] uppercase tracking-widest text-zinc-600 border border-zinc-800 bg-transparent rounded-sm px-1 py-[1px]"
        title="No sentiment snapshot yet · will populate after the next news scan"
      >
        —
      </span>
    );
  }
  const { tone, score, bullish_pct, bearish_pct, at, delta_24h, prev24h_score, prev24h_tone } = sentiment;
  const cls = tone === "bull"
    ? "border-[#00C805]/50 bg-[#00C805]/10 text-[#00C805]"
    : tone === "bear"
      ? "border-red-500/50 bg-red-500/10 text-red-300"
      : "border-zinc-700 bg-transparent text-zinc-400";
  const arrow = tone === "bull" ? "▲" : tone === "bear" ? "▼" : "▬";
  const signedScore = score > 0 ? `+${score}` : score;
  const when = at ? at.slice(0, 16).replace("T", " ") : "";
  let trend = null;
  if (delta_24h != null) {
    if (delta_24h > 0.5) trend = { arrow: "▲", cls: "text-[#00C805]", sign: "+" };
    else if (delta_24h < -0.5) trend = { arrow: "▼", cls: "text-red-400", sign: "" };
    else trend = { arrow: "▬", cls: "text-zinc-500", sign: delta_24h > 0 ? "+" : "" };
  }
  const trendTitle = trend
    ? ` · 24h ${trend.sign}${delta_24h}pp (was ${prev24h_score}${prev24h_tone ? `, ${prev24h_tone}` : ""})`
    : "";

  // Iter 82 — 7-day series of bull% - bear% for the popover.
  const netScores = (historySeries || []).map((s) =>
    Math.round(((s.bullish_pct || 0) - (s.bearish_pct || 0)) * 10) / 10,
  );
  // Iter 83 — Sparkline Hover Ticks. One annotation per point with a
  // per-day <title> so users can hover any tick and read the exact
  // bull/bear split, not just the trajectory. `label` is left blank
  // so the ticks stay subtle (2px dots) — same as prior versions.
  const sparklineAnnotations = (historySeries || []).map((s) => {
    let day = "";
    try {
      day = new Date(s.at).toLocaleDateString(undefined, {
        weekday: "short", month: "short", day: "numeric",
      });
    } catch { day = (s.at || "").slice(0, 10); }
    return {
      title: `${day} · bull ${s.bullish_pct}% · bear ${s.bearish_pct}%`,
      label: "",
    };
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <span
          data-testid={`sentiment-chip-${symbol}`}
          data-tone={tone}
          data-score={score}
          data-delta-24h={delta_24h ?? ""}
          data-prev-tone={prev24h_tone ?? ""}
          className={`mono text-[9px] uppercase tracking-widest border rounded-sm px-1 py-[1px] inline-flex items-center gap-1 tabular-nums cursor-pointer ${cls}`}
          title={`${symbol} · bull ${bullish_pct}% / bear ${bearish_pct}% · net ${signedScore}pp · ${when}${trendTitle}`}
        >
          <span aria-hidden="true">{arrow}</span>
          {signedScore}
          {trend && (
            <span
              data-testid={`sentiment-chip-trend-${symbol}`}
              data-trend={delta_24h > 0.5 ? "up" : delta_24h < -0.5 ? "down" : "flat"}
              className={`inline-flex items-center gap-0.5 border-l pl-1 ml-0.5 border-current/30 ${trend.cls}`}
              aria-label={`24 hour change ${trend.sign}${delta_24h} percentage points`}
            >
              <span aria-hidden="true">{trend.arrow}</span>
              <span className="tabular-nums text-[8px]">{trend.sign}{delta_24h}</span>
            </span>
          )}
        </span>
      </PopoverTrigger>
      <PopoverContent
        data-testid={`sentiment-chip-popover-${symbol}`}
        align="start"
        className="bg-zinc-950 border-zinc-800 w-[260px] p-3 mono text-[11px]"
      >
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-[9px] uppercase tracking-widest text-zinc-500">Sentiment · 7 days</div>
            <div className="text-zinc-200 font-semibold">{symbol}</div>
          </div>
          <div
            data-testid={`sentiment-chip-popover-score-${symbol}`}
            className={`tabular-nums text-sm ${tone === "bull" ? "text-[#00C805]" : tone === "bear" ? "text-red-300" : "text-zinc-300"}`}
          >
            {arrow} {signedScore}pp
          </div>
        </div>
        <div data-testid={`sentiment-chip-popover-sparkline-${symbol}`} className="mb-2 relative">
          {loadingHistory && (
            <div className="text-[10px] text-zinc-600">Loading trajectory…</div>
          )}
          {!loadingHistory && netScores.length >= 2 && (
            <MiniSparklineSVG
              values={netScores}
              up={netScores[netScores.length - 1] >= netScores[0]}
              width={220}
              height={36}
              ariaLabel={`${symbol} 7-day net sentiment sparkline`}
              annotations={sparklineAnnotations}
              focusableTicks={true}
              onTickFocus={(i, xPct) => { setFocusedTick(i); setFocusedTickX(xPct); }}
              onTickBlur={() => { setFocusedTick(null); setFocusedTickX(null); }}
            />
          )}
          {/* Iter 85 — Tick Value Chip. Iter 86 — anchored below the
              focused tick via absolute positioning + edge clamping
              so the chip always sits under the user's focused day. */}
          {focusedTick != null && historySeries && historySeries[focusedTick] && (() => {
            const s = historySeries[focusedTick];
            let day = "";
            try {
              day = new Date(s.at).toLocaleDateString(undefined, {
                weekday: "short", month: "short", day: "numeric",
              });
            } catch { day = (s.at || "").slice(0, 10); }
            const net = Math.round(((s.bullish_pct || 0) - (s.bearish_pct || 0)) * 10) / 10;
            const sign = net > 0 ? "+" : "";
            // Clamp x-pct to keep the ~200px chip inside the 220px
            // sparkline. Center-anchored via translateX(-50%), then
            // clamp so the mid-line stays between 22% and 78%.
            let xPct = focusedTickX;
            if (xPct == null) xPct = 50;
            const clampedX = Math.min(78, Math.max(22, xPct));
            return (
              <div
                data-testid={`sparkline-tick-chip-${symbol}`}
                data-index={focusedTick}
                data-x-pct={Math.round(xPct * 10) / 10}
                data-clamped-x={clampedX}
                style={{ left: `${clampedX}%`, transform: "translateX(-50%)" }}
                className="absolute top-full mt-1 z-10 mono text-[10px] text-zinc-300 border border-zinc-800 bg-zinc-900/95 rounded-sm px-2 py-1 tabular-nums whitespace-nowrap shadow-sm"
                role="note"
              >
                <span className="text-zinc-500">{day} · </span>
                <span className="text-[#00C805]">bull {s.bullish_pct}%</span>
                <span className="text-zinc-500"> · </span>
                <span className="text-red-300">bear {s.bearish_pct}%</span>
                <span className="text-zinc-500"> · net </span>
                <span className={net >= 0 ? "text-[#00C805]" : "text-red-300"}>{sign}{net}pp</span>
              </div>
            );
          })()}
          {!loadingHistory && netScores.length < 2 && (
            <div className="text-[10px] text-zinc-600">
              Not enough history yet — need at least 2 snapshots.
            </div>
          )}
        </div>
        <div className="grid grid-cols-3 gap-1.5 text-[9px] uppercase tracking-widest text-zinc-500 tabular-nums">
          <div>
            <div>Bull</div>
            <div className="text-[#00C805]/80 tracking-normal normal-case text-[10px] mt-0.5">{bullish_pct}%</div>
          </div>
          <div>
            <div>Bear</div>
            <div className="text-red-300/80 tracking-normal normal-case text-[10px] mt-0.5">{bearish_pct}%</div>
          </div>
          <div>
            <div>24h Δ</div>
            <div className={`tracking-normal normal-case text-[10px] mt-0.5 ${trend?.cls || "text-zinc-400"}`}>
              {trend ? `${trend.sign}${delta_24h}pp` : "—"}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default SymbolSentimentChip;
